/** @file NumberInput: a number field that commits on blur or Enter (form submit), never per keystroke. */
import type { CSSProperties, ReactElement } from 'react';
import { useEffect, useState } from 'react';

/** The shown value, the commit callback (called only with a finite, changed number) and display options. */
export type NumberInputProps = {
  value: number; onCommit: (value: number) => void; label: string;
  step?: number; disabled?: boolean; muted?: boolean; width?: number;
  /** Hover text (the Transform rows name Pose3D's axes here). */
  title?: string;
};

/** Inline-form number input: typing edits a local draft, blur or Enter commits it, an invalid draft reverts. */
export function NumberInput(props: NumberInputProps): ReactElement {
  const { value, onCommit } = props;
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(String(value)); }, [value]);
  const commit = (): void => {
    const n = Number(text);
    if (text.trim() === '' || !Number.isFinite(n)) { setText(String(value)); return; }
    if (n !== value) onCommit(n);
  };
  const style: CSSProperties = {
    width: props.width ?? 56, background: 'var(--input-bg, #11131a)', color: props.muted === true ? 'var(--muted, #8b919e)' : 'inherit',
    border: '1px solid var(--border, #2a2e38)', borderRadius: 3, padding: '1px 4px', font: 'inherit',
  };
  return (
    <form style={{ display: 'inline' }} onSubmit={(e) => { e.preventDefault(); commit(); }}>
      <input type="number" aria-label={props.label} title={props.title} value={text} step={props.step ?? 'any'} disabled={props.disabled}
        onChange={(e) => { setText(e.currentTarget.value); }} onBlur={commit} style={style} />
    </form>
  );
}
